// Un onglet ouvert avant un déploiement garde l'ancien index.html, qui référence
// des chunks que Netlify ne sert plus : la règle SPA de `_redirects` renvoie
// index.html à leur place, le navigateur refuse ce « module » HTML et la page lazy
// tombe sur « Erreur inconnue » jusqu'au refresh. Vite signale cet échec par
// `vite:preloadError` : on recharge la page pour récupérer le nouvel index.html.
// Une seule fois par fenêtre de temps, pour ne pas boucler si le chunk manque
// vraiment ; l'erreur suit alors son cours vers l'ErrorBoundary.

export const STALE_CHUNK_RELOAD_KEY = 'nabu.staleChunkReloadAt'
export const STALE_CHUNK_RELOAD_WINDOW_MS = 10_000

type ReloadDeps = {
  storage: Pick<Storage, 'getItem' | 'setItem'> | null
  reload: () => void
  now: () => number
}

/** Recharge la page si aucun rechargement n'a eu lieu récemment. Renvoie `true` s'il recharge. */
export function reloadOnceAfterStaleChunk({
  storage,
  reload,
  now,
}: ReloadDeps): boolean {
  // Stockage indisponible (navigation privée, données bloquées) : sans garde,
  // on ne recharge pas, plutôt que de risquer une boucle.
  if (!storage) return false
  try {
    const lastReloadAt = Number(storage.getItem(STALE_CHUNK_RELOAD_KEY))
    if (lastReloadAt && now() - lastReloadAt < STALE_CHUNK_RELOAD_WINDOW_MS) {
      return false
    }
    storage.setItem(STALE_CHUNK_RELOAD_KEY, String(now()))
  } catch {
    return false
  }
  reload()
  return true
}

function getSessionStorage(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

// Vite relance l'erreur après l'événement, même quand on recharge : elle atteint Sentry avant
// que la page parte. `sentryBeforeSend` l'écarte tant que ce rechargement est en cours ; celle
// d'un rechargement refusé (fenêtre de temps) part, c'est elle qui dit que le chunk manque vraiment.
let staleChunkReloadPending = false

export function isStaleChunkReloadPending(): boolean {
  return staleChunkReloadPending
}

export function installStaleChunkReload(): void {
  window.addEventListener('vite:preloadError', () => {
    const reloading = reloadOnceAfterStaleChunk({
      storage: getSessionStorage(),
      reload: () => window.location.reload(),
      now: () => Date.now(),
    })
    if (reloading) staleChunkReloadPending = true
  })
}
